import React, { useState, useEffect } from 'react';
import { Save, Plus, Edit2, Trash2, CreditCard } from 'lucide-react';
import { useOrganizador } from '../../contexts/OrganizadorContext';
import { useToast } from '../../hooks/useToast';
import api from '../../services/api';

const DadosBancarios = () => {
  const { organizadorAtivo } = useOrganizador();
  const toast = useToast();
  
  const [dadosBancarios, setDadosBancarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    banco: '',
    agencia: '',
    conta: '',
    tipo_conta: 'corrente',
    pix: '',
    observacoes: '',
    is_principal: false
  });

  useEffect(() => {
    if (organizadorAtivo?.id) {
      carregarDadosBancarios();
    }
  }, [organizadorAtivo]);

  const carregarDadosBancarios = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/organizadores/${organizadorAtivo.id}/dados-bancarios`);
      setDadosBancarios(response.data);
    } catch (error) {
      console.error('Erro ao carregar dados bancários:', error);
      toast.error('Erro ao carregar dados bancários');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        await api.put(`/organizadores/${organizadorAtivo.id}/dados-bancarios/${editingId}`, formData);
        toast.success('Dados bancários atualizados com sucesso!');
      } else {
        await api.post(`/organizadores/${organizadorAtivo.id}/dados-bancarios`, formData);
        toast.success('Dados bancários cadastrados com sucesso!');
      }
      
      setShowModal(false);
      resetForm();
      carregarDadosBancarios();
    } catch (error) {
      console.error('Erro ao salvar dados bancários:', error);
      toast.error('Erro ao salvar dados bancários');
    }
  };

  const handleEdit = (dados) => {
    setFormData(dados);
    setEditingId(dados.id);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setDeletingId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/organizadores/${organizadorAtivo.id}/dados-bancarios/${deletingId}`);
      toast.success('Dados bancários excluídos com sucesso!');
      carregarDadosBancarios();
    } catch (error) {
      console.error('Erro ao excluir dados bancários:', error);
      toast.error('Erro ao excluir dados bancários');
    } finally {
      setShowDeleteModal(false);
      setDeletingId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      banco: '',
      agencia: '',
      conta: '',
      tipo_conta: 'corrente',
      pix: '',
      observacoes: '',
      is_principal: false
    });
    setEditingId(null);
  };

  const handleNewDados = () => {
    resetForm();
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="w-full px-6 py-6 space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl px-6 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white">Dados Bancários</h1>
              <p className="text-blue-100 mt-1">Configure suas contas para recebimento de comissões</p>
            </div>
            <button
              onClick={handleNewDados}
              className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-100 text-blue-600 rounded-lg transition-colors font-medium"
            >
              <Plus size={16} />
              Nova Conta
            </button>
          </div>
        </div>

        {/* Lista de Dados Bancários */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6">
            {dadosBancarios.length === 0 ? (
              <div className="text-center py-12">
                <CreditCard size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma conta cadastrada</h3>
                <p className="text-gray-600 mb-4">Cadastre suas contas bancárias para recebimento de comissões</p>
                <button
                  onClick={handleNewDados}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Cadastrar Primeira Conta
                </button>
              </div>
            ) : (
              <div className="grid gap-4">
                {dadosBancarios.map((dados) => (
                  <div key={dados.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-gray-900">{dados.banco}</h3>
                          {dados.is_principal && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium">
                              Principal
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Agência:</span> {dados.agencia}
                          </div>
                          <div>
                            <span className="font-medium">Conta:</span> {dados.conta}
                          </div>
                          <div>
                            <span className="font-medium">Tipo:</span> {dados.tipo_conta}
                          </div>
                          <div>
                            <span className="font-medium">PIX:</span> {dados.pix || '-'}
                          </div>
                        </div>
                        {dados.observacoes && (
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="font-medium">Obs:</span> {dados.observacoes}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => handleEdit(dados)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(dados.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Excluir"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingId ? 'Editar Dados Bancários' : 'Novos Dados Bancários'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Banco *
                  </label>
                  <input
                    type="text"
                    value={formData.banco}
                    onChange={(e) => setFormData(prev => ({ ...prev, banco: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Agência *
                    </label>
                    <input
                      type="text"
                      value={formData.agencia}
                      onChange={(e) => setFormData(prev => ({ ...prev, agencia: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Conta *
                    </label>
                    <input
                      type="text"
                      value={formData.conta}
                      onChange={(e) => setFormData(prev => ({ ...prev, conta: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Conta
                  </label>
                  <select
                    value={formData.tipo_conta}
                    onChange={(e) => setFormData(prev => ({ ...prev, tipo_conta: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="corrente">Corrente</option>
                    <option value="poupanca">Poupança</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    PIX
                  </label>
                  <input
                    type="text"
                    value={formData.pix}
                    onChange={(e) => setFormData(prev => ({ ...prev, pix: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="CPF, e-mail, telefone ou chave aleatória"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                  </label>
                  <textarea
                    value={formData.observacoes}
                    onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    placeholder="Observações sobre esta conta bancária..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_principal"
                    checked={formData.is_principal}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_principal: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_principal" className="ml-2 text-sm text-gray-700">
                    Conta principal
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    <Save size={16} />
                    {editingId ? 'Atualizar' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmar Exclusão</h3>
              <p className="text-gray-600 mb-6">
                Tem certeza que deseja excluir estes dados bancários? Esta ação não pode ser desfeita.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DadosBancarios;